import CONFIG from 'Config/settings';
import { readAuthTokenCookie } from 'Utils/auth/authUtils';
import { PROFILE_TYPES } from 'Redux/profile/profileConstants';
import {
  updateProfileRequest,
  getProfileResponse,
  updateProfileCMSResponse
} from './transformers';
import {
  getProfileSettings,
  getProfileSettingsString,
  getUpdateCustomerPersonalInfoString,
  getAddressString,
  getHouseHoldString,
  getContactMethodString
} from './graphqlTransformer';
import { LoginResponse } from '../auth/models';

export default class CMSProfileApiClient {
  buildHeaders({ withContentType = false, withSkipEmails = false } = {}) {
    const authTokenCookie = readAuthTokenCookie();
    return {
      ...this.headers,
      'x-client-id': `${CONFIG.API.CMS.CLIENT_ID}`,
      ...(authTokenCookie && { Authorization: `Bearer ${authTokenCookie}` }),
      ...(withContentType && { 'Content-Type': 'application/json' }),
      ...(withSkipEmails &&
        CONFIG.FEATURE.ENABLE_SKIPEMAILS_HEADER && { skipemails: 'true' })
    };
  }

  getProfile(userId, loginId, partyUid) {
    if (!partyUid) {
      throw new Error('User ID must be provided to profile fetch');
    }

    return fetch(
      `${CONFIG.API.CMS.HOST}/api/profile/irw/${CONFIG.COUNTRY}/${partyUid}/`,
      {
        method: 'GET',
        headers: this.buildHeaders()
      }
    )
      .then(response => {
        if (response.status >= 200 && response.status <= 299) {
          return response.json();
        }
        throw Error();
      })
      .then(body => {
        const profile = getProfileResponse(body);
        profile.userId = userId;
        profile.userLogin = loginId;
        return profile;
      });
  }

  getProfileGraphql(partyUid) {
    if (!partyUid) {
      throw new Error('User ID must be provided to profile fetch');
    }
    return fetch(`${CONFIG.API.CMS.HOST}/api/profile/graphql`, {
      method: 'POST',
      credentials: 'include',
      headers: this.buildHeaders({ withContentType: true }),
      body: JSON.stringify({
        query: `{getCustomerProfile(retailUnit: ${CONFIG.COUNTRY} ) {customerIdentity { firstName lastName mobile email memberId phoneVerified emailVerified loyaltyProgramCode preferredStore zipCode }}}`
      })
    })
      .then(response => {
        if (response.status >= 200 && response.status <= 299) {
          return response.json();
        }
        throw Error();
      })
      .then(body => {
        if (body?.data?.getCustomerProfile?.customerIdentity) {
          return body.data.getCustomerProfile.customerIdentity;
        }
        throw Error();
      });
  }

  update(currentProfile, updatedProfile, partyUid, mode) {
    const profileData = JSON.stringify(
      updateProfileRequest(currentProfile, updatedProfile)
    );
    const serviceUrl = `${CONFIG.API.CMS.HOST}/api/profile/irw/${CONFIG.COUNTRY}/${partyUid}/`;
    return new Promise((resolve, reject) => {
      fetch(mode ? `${serviceUrl}?mode=${mode}` : serviceUrl, {
        method: 'PUT',
        headers: this.buildHeaders({
          withContentType: true,
          withSkipEmails: true
        }),
        body: profileData
      })
        // eslint-disable-next-line consistent-return
        .then(response => {
          if (response.status >= 200 && response.status <= 299) {
            resolve({ response: { ok: true }, request: updatedProfile });
          }
          if (response.status >= 400 && response.status <= 499) {
            return response.json();
          }
          // Internal server error
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({ response, request: updatedProfile });
        })
        .then(body => {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({
            response: updateProfileCMSResponse(body),
            request: updatedProfile
          });
        })
        .catch(error => {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({ response: error, request: updatedProfile });
        });
    });
  }

  updateProfileSettings(currentProfile, updatedProfile, partyUid) {
    if (!partyUid) {
      throw new Error('User ID must be provided to profile update');
    }
    const profileSettings = getProfileSettings(currentProfile, updatedProfile);
    const profileSettingString = getProfileSettingsString(profileSettings);
    return fetch(`${CONFIG.API.CMS.HOST}/api/profile/graphql`, {
      method: 'POST',
      credentials: 'include',
      headers: this.buildHeaders({ withContentType: true }),
      body: JSON.stringify({
        query: `mutation {updateProfileSetting(retailUnit:  ${CONFIG.COUNTRY}, ${profileSettingString}  ) {success}}`
      })
    })
      .then(response => {
        if (response.status >= 200 && response.status <= 299) {
          return response.json();
        }
        throw Error();
      })
      .then(body => {
        if (!body.errors && body.data.updateProfileSetting.success) {
          return true;
        }
        throw body.errors;
      });
  }

  updateCustomerPersonalInfo(profile, currentProfile) {
    const customerPersonalInfoString = getUpdateCustomerPersonalInfoString(
      profile,
      currentProfile
    );
    return fetch(`${CONFIG.API.CMS.HOST}/api/profile/graphql`, {
      method: 'POST',
      credentials: 'include',
      headers: this.buildHeaders({ withContentType: true }),
      body: JSON.stringify({
        query: `mutation {updateCustomerPersonalInfo(retailUnit:${CONFIG.COUNTRY}, ${customerPersonalInfoString}){success}}`
      })
    })
      .then(response => {
        if (response.status >= 200 && response.status <= 299) {
          return response.json();
        }
        throw Error();
      })
      .then(body => {
        if (!body.errors && body.data.updateCustomerPersonalInfo.success) {
          return true;
        }
        throw new Error(body?.errors[0]?.errorCode);
      });
  }

  upsertBillingAddress(newPrimaryAddress, formId) {
    if (!formId) {
      throw new Error('Form ID must be provided to profile update');
    }
    const newPrimaryAddressString = getAddressString(newPrimaryAddress);
    return fetch(`${CONFIG.API.CMS.HOST}/api/profile/graphql`, {
      method: 'POST',
      credentials: 'include',
      headers: this.buildHeaders({
        withContentType: true,
        withSkipEmails: true
      }),
      body: JSON.stringify({
        query: `mutation {upsertBillingAddress(retailUnit: ${CONFIG.COUNTRY}, ${newPrimaryAddressString}) {success}}`
      })
    })
      .then(response => {
        if (response.status >= 200 && response.status <= 299) {
          return response.json();
        }
        throw Error();
      })
      .then(body => {
        if (!body.errors && body.data.upsertBillingAddress.success) {
          return true;
        }
        throw body.errors;
      });
  }

  updateHomeAndInterests(homeAndInterest, partyUid) {
    if (!partyUid) {
      throw new Error('User ID must be provided to profile update');
    }
    const homeAndInterestString = getHouseHoldString(homeAndInterest);
    return fetch(`${CONFIG.API.CMS.HOST}/api/profile/graphql`, {
      method: 'POST',
      credentials: 'include',
      headers: this.buildHeaders({
        withContentType: true,
        withSkipEmails: true
      }),
      body: JSON.stringify({
        query: `mutation {updateHomeAndInterests(retailUnit: ${CONFIG.COUNTRY}, ${homeAndInterestString}) {success}}`
      })
    })
      .then(response => {
        if (response.status >= 200 && response.status <= 299) {
          return response.json();
        }
        throw Error();
      })
      .then(body => {
        if (!body.errors && body.data.updateHomeAndInterests.success) {
          return true;
        }
        throw body.errors;
      });
  }

  upsertContactMethod(contactMethod, partyUid) {
    if (!partyUid) {
      throw new Error('User ID must be provided to profile update');
    }
    const contactMethodString = getContactMethodString(contactMethod);
    return fetch(`${CONFIG.API.CMS.HOST}/api/profile/graphql`, {
      method: 'POST',
      credentials: 'include',
      headers: this.buildHeaders({
        withContentType: true,
        withSkipEmails: true
      }),
      body: JSON.stringify({
        query: `mutation {upsertContactMethod(retailUnit: ${CONFIG.COUNTRY}, ${contactMethodString}) {success}}`
      })
    })
      .then(response => {
        if (response.status >= 200 && response.status <= 299) {
          return response.json();
        }
        throw Error();
      })
      .then(body => {
        if (!body.errors && body.data.upsertContactMethod.success) {
          return true;
        }
        throw body.errors;
      });
  }

  createDeliveryAddress(newDeliveryAddress) {
    const newDeliveryAddressString = getAddressString(newDeliveryAddress);
    return fetch(`${CONFIG.API.CMS.HOST}/api/profile/graphql`, {
      method: 'POST',
      credentials: 'include',
      headers: this.buildHeaders({
        withContentType: true,
        withSkipEmails: true
      }),
      body: JSON.stringify({
        query: `mutation {createDeliveryAddress(retailUnit: ${CONFIG.COUNTRY}, ${newDeliveryAddressString}) {success}}`
      })
    })
      .then(response => {
        if (response.status >= 200 && response.status <= 299) {
          return response.json();
        }
        throw Error();
      })
      .then(body => {
        if (!body.errors && body.data.createDeliveryAddress.success) {
          return true;
        }
        throw body.errors;
      });
  }

  deleteDeliveryAddress(partyUid, addressId) {
    if (!partyUid) {
      throw new Error('User ID must be provided to profile update');
    }
    return fetch(`${CONFIG.API.CMS.HOST}/api/profile/graphql`, {
      method: 'POST',
      credentials: 'include',
      headers: this.buildHeaders({ withContentType: true }),
      body: JSON.stringify({
        query: `mutation {deleteDeliveryAddress(retailUnit:  ${CONFIG.COUNTRY}, id: "${addressId}" ) {success}}`
      })
    })
      .then(response => {
        if (response.status >= 200 && response.status <= 299) {
          return response.json();
        }
        throw Error();
      })
      .then(body => {
        if (!body.errors && body.data.deleteDeliveryAddress.success) {
          return true;
        }
        throw body.errors;
      });
  }

  upgrade(currentProfile, updatedProfile, partyUid) {
    const profileData = JSON.stringify(
      updateProfileRequest(currentProfile, updatedProfile)
    );
    return new Promise((resolve, reject) => {
      fetch(
        `${CONFIG.API.CMS.HOST}/api/profile/irw/${CONFIG.COUNTRY}/${CONFIG.LANGUAGE}/upgrade/${partyUid}/`,
        {
          method: 'PUT',
          headers: this.buildHeaders({ withContentType: true }),
          body: profileData
        }
      )
        // eslint-disable-next-line consistent-return
        .then(response => {
          if (response.status >= 200 && response.status <= 299) {
            resolve({ response: { ok: true }, request: updatedProfile });
          }
          if (response.status >= 400 && response.status <= 499) {
            return response.json();
          }
          // Internal server error
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({ response, request: updatedProfile });
        })
        .then(body => {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({
            response: updateProfileCMSResponse(body),
            request: updatedProfile
          });
        })
        .catch(error => {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({ response: error, request: updatedProfile });
        });
    });
  }

  extractUserInfo(user, decodedToken) {
    const response = new LoginResponse();
    const {
      memberId,
      firstName,
      lastName,
      email,
      mobile,
      phoneVerified,
      emailVerified,
      loyaltyProgramCode,
      preferredStore,
      zipCode
    } = user;

    response.authenticated = true;
    response.user = {
      userId: memberId,
      userLogin: email || mobile,
      firstName,
      lastName,
      preferredStore,
      zipCode,
      email,
      mobile,
      partyUid: decodedToken['https://accounts.ikea.com/partyUId'],
      userName: `${user.firstName} ${user.lastName}`,
      phoneVerified,
      emailVerified,
      profileType: this.getLoyaltyProgram(
        loyaltyProgramCode,
        decodedToken['https://accounts.ikea.com/customerType']
      ),
      identifier: !email && mobile && phoneVerified ? 'mobile' : 'email',
      employeeId: decodedToken['https://accounts.ikea.com/employeeID']
    };
    return response;
  }

  // eslint-disable-next-line class-methods-use-this
  getLoyaltyProgram(loyaltyProgramCode, customerType) {
    if (customerType === 'business') {
      return PROFILE_TYPES.BUSINESS;
    }
    if (loyaltyProgramCode?.startsWith('FAMILY')) {
      return PROFILE_TYPES.FAMILY;
    }
    return PROFILE_TYPES.REGULAR;
  }

  updateDeliveryAddress(address) {
    const addressData = getAddressString(address);
    return fetch(`${CONFIG.API.CMS.HOST}/api/profile/graphql`, {
      method: 'POST',
      credentials: 'include',
      headers: this.buildHeaders({
        withContentType: true,
        withSkipEmails: true
      }),
      body: JSON.stringify({
        query: `mutation {updateDeliveryAddress(retailUnit:  ${CONFIG.COUNTRY}, id: "${address.addressId}", ${addressData} ) {success}}`
      })
    })
      .then(response => {
        if (response.status >= 200 && response.status <= 299) {
          return response.json();
        }
        throw Error();
      })
      .then(body => {
        if (!body.errors && body.data.updateDeliveryAddress.success) {
          return true;
        }
        throw body.errors;
      });
  }
}
